import type { ReportParameterValidValue } from '@/types';
import { sameValues, type DefaultInfo } from './reportDefault';
import { describeDateRelationship } from './dateRelationship';
import { optionFor } from './valueMatching';

/**
 * The report's own default for one parameter, kept strictly apart from the value stored on the
 * subscription.
 *
 * That separation is the whole point of this panel. The control above always holds what the
 * subscription stores; this only ever talks about what the report would do instead, and says which
 * of the two it is describing every time. An earlier version put the default in the input's
 * placeholder, where a stored value and a report default appeared in the same place in nearly the
 * same grey — so a user could not tell whether a field was set at all. It then spent two lines of
 * grey under every field saying so instead, which is how ten parameters became a page you had to
 * scroll. The facts are unchanged; they now sit behind the affordance on the label row.
 *
 * Resetting CLEARS the stored value rather than copying the default into it. That is what "use the
 * report's default" actually means to SSRS: a subscription that names a parameter without giving
 * it a value gets whatever the report decides at run time, which is how 270 of the 282
 * subscriptions on MANDW01 already handle their dates. Copying today's computed value in would
 * freeze it, quietly turning a report that always covers the last 60 days into one pinned to the
 * 60 days after whichever afternoon someone opened this page.
 *
 * A computed DateTime value additionally gets a relationship word — "today", "30 days ago",
 * "first of last month" — ahead of the literal, because the literal alone is a date next month
 * with nothing to say whether that window is the right one; see dateRelationship.ts. Two things
 * guard against misreading a value that is not really a moving date: `relationOf` only fires for
 * `info.kind === 'computed'`, never `fixed`, and only for a value that is not one of this
 * parameter's own `options`. Both matter for "Scope Version" on /Sales/Scope 3 — DateTime too, but
 * its default is fixed rather than computed AND its nine values are a fixed enumeration (one reads
 * "Rates at time of travel") rather than a moving date, so describing its 1900-01-01 sentinel as
 * "126 years ago" would be wrong on both counts, not just one. A value the option list does not
 * recognise is left to the day math; one it does recognise keeps its label, exactly as before.
 */
export function ReportDefaultPanel({
  id,
  info,
  stored,
  options,
  parameterType,
  rememberedLabel,
  onReset,
  disabled
}: {
  id: string;
  info: DefaultInfo;
  stored: string[];
  options: ReportParameterValidValue[];
  /** The SSRS parameter type. Only a plain "DateTime" — not one of `options` — earns a relationship word. */
  parameterType: string;
  /** A label seen in an earlier answer, or undefined if this value has never resolved one. */
  rememberedLabel: (value: string) => string | undefined;
  onReset: () => void;
  disabled: boolean;
}) {
  if (info.kind === 'none') return null;

  // Falls back to the raw value itself, not unmatchedLabel's wording: this line describes what
  // the REPORT defaults to, not a stored choice, so "no longer offered" would misname a default
  // the report is still perfectly happy to run with — it just is not one optionFor recognises.
  const label = (v: string) => optionFor(v, options)?.label ?? rememberedLabel(v) ?? v;
  const values = info.kind === 'fixed' || info.kind === 'computed' ? info.values : null;
  const matches = values !== null && sameValues(stored, values);

  // Only for a computed default: a fixed one is a constant the report author chose, not a moving
  // target, and running "01/01/2020" through day math would answer a question nobody asked.
  const relationOf = (v: string): string | null =>
    info.kind === 'computed' && parameterType === 'DateTime' && !optionFor(v, options)
      ? describeDateRelationship(v, new Date())
      : null;

  return (
    <div id={id} className="param-info-panel">
      <span>
        {info.kind === 'fixed' && <>The report&rsquo;s own default is </>}
        {info.kind === 'computed' && <>Computed each run; right now that resolves to </>}
        {info.kind === 'computed-unknown' && <>Computed each run, so its value is only settled then.</>}
        {values !== null && (
          <>
            {values.map((v, i) => {
              const relation = relationOf(v);
              return (
                <span key={`${v}-${i}`}>
                  {i > 0 && ', '}
                  {relation && <><span className="param-default-relation">{relation}</span> (</>}
                  <span className="param-default-value">{label(v)}</span>
                  {relation && <>)</>}
                </span>
              );
            })}.
          </>
        )}
      </span>

      {stored.length === 0 ? (
        <span className="param-default-match">Nothing is set here, so that is what runs.</span>
      ) : matches ? (
        <span className="param-default-match">The same as the value above.</span>
      ) : !disabled ? (
        <button type="button" className="param-default-reset" onClick={onReset}>
          Use the report&rsquo;s default
        </button>
      ) : null}
    </div>
  );
}
